import { Provider } from "@redotech/redo-model/order";
import type { TrackingPageOrder } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import {
  SUNDAYRED_TEAMS,
  TAYLORMADE_TEAMS,
} from "@redotech/redo-model/taylormade/constants";
import { TrackableType } from "@redotech/redo-model/trackable";
import { assertNever } from "@redotech/util/type";

interface ReturnAppSettings {
  storeUrl: string;
  merchantName: string;
  useShopifyStorefrontForCommentSold: boolean;
  exchanges: { shopSiteDomain: string; shopSiteURL: string };
}

export interface UpsellRedirectHandler {
  logUpsellAndRedirect: (selectedVariant?: {
    id: string;
    handle: string;
    productUrl?: string | null;
  }) => void;
}

export interface UpsellRedirectParams {
  order: TrackingPageOrder;
  selectWindow: React.MutableRefObject<Window>;
  returnAppSettings: ReturnAppSettings;
  logPageClick: (params: {
    trackableId: string;
    trackableType: TrackableType;
    eventType: string;
    url: string;
    image?: string;
  }) => Promise<void>;
  redirectToWebsiteWithDiscountCode?: (params: {
    discountCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmContent?: string;
    utmTerm?: string;
    settings: ReturnAppSettings;
    window: React.MutableRefObject<Window>;
    selectedProduct?: { url: string; variantId: string };
  }) => void;
  discountCode?: string;
  redirectUrl?: string;
  redirectToProductPage?: boolean;
}

export function createUpsellRedirectHandler(
  params: UpsellRedirectParams,
): UpsellRedirectHandler {
  const source = params.order.source;

  switch (source) {
    case Provider.SHOPIFY:
      return createShopifyRedirectHandler(params);
    case Provider.BIGCOMMERCE:
      return createBigCommerceRedirectHandler(params);
    case Provider.COMMENTSOLD:
      return createCommentsoldRedirectHandler(params);
    case Provider.COMMERCE_CLOUD:
    case Provider.SFOM:
      return createCommerceCloudRedirectHandler(params);
    default:
      assertNever(source);
  }
}

function createShopifyRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
  redirectToWebsiteWithDiscountCode,
  discountCode,
  redirectUrl,
  redirectToProductPage,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      if (redirectToProductPage && redirectUrl) {
        const url = new URL(redirectUrl);
        if (selectedVariant) {
          url.searchParams.set("variant", selectedVariant.id);
        }
        const w = open(url);
        if (w) {
          w.onunload = () => {
            if (w.closed) {
              // @ts-expect-error: strict-null-checks
              selectWindow.current = undefined;
            }
          };
          selectWindow.current = w;
        }
        return;
      }

      redirectToWebsiteWithDiscountCode?.({
        utmSource: "redo",
        utmMedium: "order_tracking",
        utmContent: "upsell",
        utmTerm: order.internalMongoId,
        settings: returnAppSettings,
        window: selectWindow,
        discountCode,
        selectedProduct:
          redirectUrl && selectedVariant
            ? { url: redirectUrl, variantId: selectedVariant.id }
            : undefined,
      });
    },
  };
}

function createBigCommerceRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const url = selectedVariant
        ? new URL(
            `https://${returnAppSettings.storeUrl}${selectedVariant.handle}`,
          )
        : new URL(`https://${returnAppSettings.storeUrl}`);

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}

function createCommentsoldRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
      productUrl?: string | null;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const url = (() => {
        if (!selectedVariant) {
          return new URL(`https://${returnAppSettings.storeUrl}`);
        }
        if (selectedVariant.productUrl) {
          const url = new URL(`${selectedVariant.productUrl}`);
          url.search = `${
            url.search ? url.search + "&" : "?"
          }variant=${selectedVariant.id}`;
          return url;
        } else {
          throw new Error("Missing url for commentsold upsell redirect");
        }
      })();

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}

function createCommerceCloudRedirectHandler({
  order,
  selectWindow,
  returnAppSettings,
  logPageClick,
  redirectUrl,
}: UpsellRedirectParams): UpsellRedirectHandler {
  return {
    logUpsellAndRedirect: (selectedVariant?: {
      id: string;
      handle: string;
    }) => {
      logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "upsell",
        url: returnAppSettings.storeUrl,
      }).catch((error) => {
        console.error("Failed to log upsell click:", error);
      });

      const isCanadianOrder = order.shippingAddress?.countryCode === "CA";

      let baseStoreUrl = returnAppSettings.storeUrl;

      if (TAYLORMADE_TEAMS.includes(order.teamId) && isCanadianOrder) {
        baseStoreUrl = "www.taylormadegolf.ca";
      }
      if (SUNDAYRED_TEAMS.includes(order.teamId) && isCanadianOrder) {
        baseStoreUrl = "www.sundayred.ca";
      }

      const url = selectedVariant
        ? new URL(`https://${baseStoreUrl}/${selectedVariant.id}.html`)
        : new URL(`https://${baseStoreUrl}`);

      const w = open(url);
      if (w) {
        w.onunload = () => {
          if (w.closed) {
            // @ts-expect-error: strict-null-checks
            selectWindow.current = undefined;
          }
        };
        selectWindow.current = w;
      }
    },
  };
}
